import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { RootState } from "../../../../../../../redux/store";
import VerifiedIcon from "../../../../../../../assets/svg/VerifiedIcon";
import { ChartEmptyState } from "./ChartCard";
import { getHelpdeskLabels } from "../../../../../../../utils/constants/mappings";

export interface ClosedMonthData {
  month: string;
  year: string;
  value: number;
}

interface TicketsClosedMonthlyProps {
  data: ClosedMonthData[];
}

type MonthTickProps = {
  x?: number;
  y?: number;
  payload?: { value: string };
  isDarkMode?: boolean;
  data?: ClosedMonthData[];
};

const MonthTick = ({ x = 0, y = 0, payload, isDarkMode, data = [] }: MonthTickProps) => {
  const label = payload?.value ?? "";
  const entry = data?.find((d) => d?.month === label);
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={12}
        textAnchor="middle"
        fill={isDarkMode ? "#c0c2c5" : "#898989"}
        fontSize={11}
        fontWeight={500}
      >
        {label}
      </text>
      <text
        x={0}
        y={0}
        dy={24}
        textAnchor="middle"
        fill={isDarkMode ? "#898989" : "#a3a3a3"}
        fontSize={10}
        fontWeight={400}
      >
        {entry?.year}
      </text>
    </g>
  );
};

type ValueLabelProps = {
  x?: number;
  y?: number;
  width?: number;
  value?: number;
  isDarkMode?: boolean;
};

const ValueLabel = ({ x = 0, y = 0, width = 0, value, isDarkMode }: ValueLabelProps) => {
  if (!value) return null;
  return (
    <text
    x={x + width / 2}
    y={y - 6}
    textAnchor="middle"
    fill={isDarkMode ? "#fbfbfb" : "#252b37"}
    fontSize={12}
    fontWeight={500}
  >
    {value}
  </text>
  );
};

const TicketsClosedMonthly = ({ data }: TicketsClosedMonthlyProps) => {
  const region = useSelector(
    (state: RootState) => state?.auth?.client?.region
  );
  const helpdeskLabels = getHelpdeskLabels(region ?? "UK");
  const isDarkMode = useSelector(
    (state: RootState) => state?.themeProvider?.isDarkMode
  );
  const gridStroke = isDarkMode ? "#393f4a" : "#e9eaeb";

  const maxValue = useMemo(() => {
    if (!data?.length) return 10;
    const max = Math.max(...data.map((d) => d.value));
    if (max <= 5) return 5;
    if (max <= 10) return 10;
    const rawStep = Math.pow(10, Math.floor(Math.log10(max))) / 2;
    const step = Math.max(5, Math.ceil(rawStep / 5) * 5);
    return Math.ceil(max / step) * step;
  }, [data]);

  const yTicks = useMemo(() => {
    const step = maxValue / 5;
    return Array.from({ length: 6 }, (_, i) => Math.round(i * step));
  }, [maxValue]);

  const yAxisWidth = useMemo(() => {
    return Math.max(36, String(maxValue).length * 8 + 12);
  }, [maxValue]);

  const showEmptyState = !data?.some((item) => item?.value > 0);

  return (
    <div className="bg-popper-bg dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk rounded-[8px] pt-4 px-4 pb-2 flex flex-col gap-4 min-w-0">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex gap-1 items-center">
            <VerifiedIcon className="w-4.5 h-4.5 text-analytics-green dark:text-analytics-green-dk" />
            <span className="text-base font-medium text-heading-text dark:text-heading-text-dk">
              {helpdeskLabels.ticketPlural} Closed Monthly
            </span>
          </div>
          <p className="text-xs text-dark-gray dark:text-dark-gray-dk mt-0.5">
            Count of {helpdeskLabels.ticketPluralLower} closed in each month
          </p>
        </div>

        <span className="flex items-center gap-0.5 h-[22px] pl-1 pr-2 py-0.5 rounded-md bg-analytics-green dark:bg-analytics-green-dk text-sm font-semibold text-fixed-white-light-and-dark-mode dark:text-fixed-white-light-and-dark-mode-dk uppercase leading-normal shrink-0">
          <VerifiedIcon className="w-4 h-4 text-fixed-white-light-and-dark-mode dark:text-fixed-white-light-and-dark-mode-dk" />
          Closed
        </span>
      </div>

      {showEmptyState ? (
        <ChartEmptyState height="h-[280px]" />
      ) : (
        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 16, right: 4, bottom: 8, left: 0 }}
              barSize={31}
            >
              <CartesianGrid
                horizontal={true}
                vertical={false}
                stroke={gridStroke}
              />
              <YAxis
                type="number"
                domain={[0, maxValue]}
                ticks={yTicks}
                tickLine={false}
                axisLine={{ stroke: gridStroke }}
                tick={{
                  fill: isDarkMode ? "#c0c2c5" : "#898989",
                  fontSize: 11,
                  fontWeight: 500,
                }}
                width={yAxisWidth}
              />
              <XAxis
                dataKey="month"
                tickLine={false}
                axisLine={false}
                tick={(props: MonthTickProps) => (
                  <MonthTick {...props} isDarkMode={isDarkMode} data={data} />
                )}
                interval={0}
                height={40}
              />
              <Bar
                dataKey="value"
                fill="#10b981"
                radius={[6, 6, 0, 0]}
                label={(props: ValueLabelProps) => (
                  <ValueLabel {...props} isDarkMode={isDarkMode} />
                )}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default TicketsClosedMonthly;
